import React, { useState } from "react";
import MUIDataTable from "mui-datatables";
import { useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import {
  Box,
  Button,
  Card,
  CardHeader,
  Select,
  MenuItem,
  TableContainer,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { RiDeleteBinLine, RiPencilLine } from "@remixicon/react";
import SkeletonTable from "./SkeletonTable";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
//import GoToPage from "./GoToPage";
const GenericTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  showDelete = true,
  showEdit = true,
  showAction = true,
  serverSide = false,
  count = 0,
  onPageChange = () => { },
  onPageSizeChange = () => { },
  onSearchChange = () => { },
  order,
  setOrder,
  pageTitle,
  buttons = [],
  options: parentOptions = {},
  displayColumns,
  displayRows,
  showClientSideSearch = true,
  showCientSidePagination = true,
  isTableLoading = false,
  rowsPerPageOptions = [10, 25, 50, 100], // Default value, can be overridden
  showPagination = true,
  showSearchBox = true,
  hideToolbar = false,
}) => {
  const { loading } = useSelector(state => state.root.loading);
  const [showSearch, setShowSearch] = useState(false);

  const enhancedColumns = showAction
    ? [
      ...columns,
      {
        name: "action",
        label: "Action",
        options: {
          sort: false,
          // customBodyRender: (_, tableMeta) => (
          //   <Box display="flex" alignItems="center" gap={1}>
          //     {showEdit && (
          //       <IconButton onClick={() => onEdit(tableMeta.rowData)}>
          //         <RiPencilLine />
          //       </IconButton>
          //     )}
          //     {showDelete && (
          //       <IconButton
          //         onClick={() => onDelete(tableMeta.rowData)}
          //         color="error"
          //       >
          //         <RiDeleteBinLine />
          //       </IconButton>
          //     )}
          //   </Box>
          // ),
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex]; // get full row from your data array
            return (
              <Box display="flex" alignItems="center" gap={1}>
                {showEdit && (
                  <IconButton
                    onClick={() => {
                      onEdit(tableMeta.rowData);
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth", // for smooth scrolling
                      });
                    }}
                  >
                    <RiPencilLine />
                  </IconButton>
                )}
                {/* {showDelete && (
                    <IconButton onClick={() => onDelete(tableMeta.rowData)} color="error">
                      <RiDeleteBinLine />
                    </IconButton>
                  )} */}
                {showDelete &&
                  (row?.is_default === 1 ? (
                    <span style={{ color: "gray", fontSize: "0.85rem" }}>Default</span>
                  ) : (
                    <>
                      {!row?.hideActions && (
                        <IconButton onClick={() => onDelete(tableMeta.rowData)} color="error">
                          <RiDeleteBinLine />
                        </IconButton>
                      )}
                    </>
                  ))}
              </Box>
            );
          },
          setCellProps: () => ({
            style: {
              width: 120,
              textAlign: "left",
              padding: "8px 16px",
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              width: 120,
              textAlign: "left",
              padding: "8px 26px",
              fontWeight: "bold",
            },
          }),
        },
      },
    ]
    : columns;

  // Core table options (handles both client and server side)
  const baseOptions = {
    selectableRows: "none",
    responsive: "standard",
    download: false,
    print: false,
    filter: false,
    viewColumns: false,
    draggableColumns: { enabled: false },
    pagination: showCientSidePagination,
    rowsPerPage: 100,
    rowsPerPageOptions: parentOptions.rowsPerPageOptions || rowsPerPageOptions, // Prioritize parentOptions
    sort: true,
    search: showClientSideSearch,
    customFooter: showPagination
      ? (count, page, rowsPerPage, changeRowsPerPage, changePage) => {
        const totalPages = Math.ceil(count / rowsPerPage);
        const currentPage = page + 1;
        const pageNumbers = [];
        for (
          let i = Math.max(1, currentPage - 2);
          i <= Math.min(totalPages, currentPage + 2);
          i++
        ) {
          pageNumbers.push(i);
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "0.85rem",
              justifyContent: "space-between",
              p: 2,
              width: "100%",
              gap: 3,
            }}
          >
            <Box sx={{ color: "text.secondary" }}>
              {count === 0
                ? "Showing 0 entries"
                : `Showing ${page * rowsPerPage + 1} to ${Math.min(
                  (page + 1) * rowsPerPage,
                  count
                )} of ${count} ${count === 1 ? "entry" : "entries"}`}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>Rows per page</Box>
              <Select
                value={rowsPerPage}
                onChange={e => changeRowsPerPage(e.target.value)}
                size="small"
                sx={{ minWidth: "70px" }}
              >
                {(parentOptions.rowsPerPageOptions || rowsPerPageOptions).map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {/* <GoToPage
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={changePage}
          /> */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => changePage(0)} disabled={page === 0} size="small">
                <FirstPageIcon fontSize="small" />
              </IconButton>

              <IconButton onClick={() => changePage(page - 1)} disabled={page === 0} size="small">
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>

              {pageNumbers.map(num => (
                <Button
                  key={num}
                  variant={currentPage === num ? "contained" : "text"}
                  onClick={() => changePage(num - 1)}
                  size="small"
                  sx={{ minWidth: "30px", mx: 0.5, py: 0.5, borderRadius: "50%" }}
                >
                  {num}
                </Button>
              ))}

              <IconButton
                onClick={() => changePage(page + 1)}
                disabled={page >= totalPages - 1}
                size="small"
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() => changePage(totalPages - 1)}
                disabled={page >= totalPages - 1}
                size="small"
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      }
      : () => false,
  };

  const serverSideOptions = serverSide
    ? {
      serverSide: true,
      count,
      onTableChange: (action, tableState) => {
        switch (action) {
          case "changePage":
            onPageChange(tableState.page);
            break;
          case "changeRowsPerPage":
            onPageSizeChange(tableState.rowsPerPage);
            break;
          case "search":
            onSearchChange(tableState.searchText);
            break;
          case "sort":
            if (tableState.activeColumn !== undefined) {
              setOrder({
                column: tableState.activeColumn,
                dir: tableState.sortOrder.direction,
              });
            }
            break;
          default:
            break;
        }
      },
      sortOrder:
        order?.column !== undefined
          ? {
            name: order.column,
            direction: order.dir,
          }
          : {},
    }
    : {};

  // Client-side specific options
  const clientSideOptions = !serverSide
    ? {
      searchOpen: true,
      searchPlaceholder: "Search records…",
    }
    : {};

  // Custom search box + header (only for client-side)
  const customSearchRender = !serverSide
    ? (searchText, handleSearch, hideSearch) => (
      <Box>
        <CardHeader
          title={pageTitle}
          action={
            <Box display="flex" alignItems="center" gap={2}>
              {buttons.map((btn, idx) => (
                <Button
                  key={idx}
                  variant={btn.variant || "contained"}
                  size="small"
                  onClick={btn.onClick}
                >
                  {btn.text}
                </Button>
              ))}

              {showSearchBox && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <TextField
                    variant="outlined"
                    size="small"
                    value={searchText ?? ""}
                    placeholder="Search"
                    onChange={e => handleSearch(e.target.value)}
                    onBlur={() => {
                      setTimeout(() => {
                        if (!searchText) {
                          setShowSearch(false);
                        }
                      }, 150);
                    }}
                  />
                  {/* <IconButton
                      size="small"
                      onClick={() => {
                        handleSearch("");
                        setShowSearch(false);
                      }}
                    >
                      <CloseIcon />
                    </IconButton> */}
                </Box>
              )}

            </Box>
          }
        />
      </Box>
    )
    : undefined;

  // Merge all options
  const tableOptions = {
    ...baseOptions,
    ...serverSideOptions,
    ...clientSideOptions,
    ...parentOptions, // merge user overrides last
    customSearchRender,
  };

  return loading || isTableLoading ? (
    <SkeletonTable displayColumns={displayColumns || 5} displayRows={displayRows || 10} />
  ) : (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        boxShadow: "none",
        "& .MuiToolbar-root": {
          display: hideToolbar ? "none" : "flex",
        },
      }}
    >
      <MUIDataTable
        title={pageTitle}
        data={data}
        columns={enhancedColumns}
        options={tableOptions}
      />
    </TableContainer>
  );
};

export default GenericTable;
